import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import MultiSelect from './MultiSelect'; // Your custom MultiSelect component
import DatePicker from 'react-datepicker'; // Import DatePicker
import 'react-datepicker/dist/react-datepicker.css'; // And its styles

const FormField = ({
  name, label, icon: Icon, as = 'input', type = 'text', options = [], value, onChange, error, required, helpText, loading, placeholder, component, ...rest
}) => {

  // Logic for 'switch' type
  if (as === 'switch') {
    return (
      <Form.Group controlId={`form-${name}`}>
        <Form.Check
          type="switch"
          id={`form-switch-${name}`}
          name={name}
          label={
            <>
              {Icon && <Icon className="me-2 text-primary" />}
              {label}
              {required && <span className="text-danger ms-1">*</span>}
            </>
          }
          // Handle string values ('0', '1') and boolean/number values
          checked={value === '1' || value === 1 || value === true}
          onChange={(e) => {
            // Convert boolean to string for consistency with your existing logic
            const switchValue = e.target.checked ? '1' : '0';
            // Create synthetic event to match your handleChange expectations
            const syntheticEvent = { target: { name, value: switchValue } };
            onChange(syntheticEvent);
          }}
          isInvalid={!!error}
          {...rest}
        />
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
      </Form.Group>
    );
  }

  // Logic for 'multiselect' type
  if (as === 'multiselect') {
    return (
      <Form.Group controlId={`form-${name}`}>
        {label && <Form.Label>{label}{required && <span className="text-danger">*</span>}</Form.Label>}
        <MultiSelect
          name={name}
          value={value || []}
          // We now pass the main 'onChange' (handleChange) function directly.
          // Your MultiSelect component is smart enough to work with it.
          onChange={onChange} 
          options={options}
          placeholder={placeholder || `Select ${label || ''}...`}
          isInvalid={!!error}
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </Form.Group>
    );
  }

  // Logic for a standard dropdown/select - THIS WAS MISSING!
  if (as === 'select') {
    // Check if options already include a placeholder (value="")
    const hasExistingPlaceholder = (options || []).some(opt => opt.value === "");

    return (
      <Form.Group controlId={`form-${name}`}>
        {label && <Form.Label>{label}{required && <span className="text-danger ms-1">*</span>}</Form.Label>}
        <InputGroup hasValidation>
          {Icon && <InputGroup.Text><Icon /></InputGroup.Text>}
          <Form.Select
            name={name}
            value={value ?? ''}
            onChange={onChange} // The main handler works fine for standard selects
            isInvalid={!!error}
            {...rest}
          >
            {!hasExistingPlaceholder && ( // Only add if no placeholder exists in options
              <option value="">-- Select {label} --</option>
            )}
            {(options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        </InputGroup>
        {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
      </Form.Group>
    );
  }

  // Special handling for DatePicker if a component is passed
  // This is used by AcademicYear.jsx
  if (type === 'date' && component === DatePicker) {
    return (
      <Form.Group controlId={`form-${name}`}>
        {label && <Form.Label>{label}{required && <span className="text-danger ms-1">*</span>}</Form.Label>}
        <InputGroup hasValidation>
          {Icon && <InputGroup.Text><Icon /></InputGroup.Text>}
          <DatePicker
            selected={value}
            onChange={(date) => {
              // Create a synthetic event to be compatible with the generic handleChange
              const syntheticEvent = { target: { name, value: date } };
              onChange(syntheticEvent);
            }}
            className={`form-control ${error ? 'is-invalid' : ''}`}
            dateFormat="dd/MM/yyyy"
            placeholderText={`Select ${label}`}
            autoComplete="off"
            {...rest}
          />
          {error && <div className="invalid-feedback d-block">{error}</div>}
        </InputGroup>
      </Form.Group>
    );
  }

  // Logic for all other inputs (text, password, textarea, etc.)
  return (
    <Form.Group controlId={`form-${name}`}>
      {label && <Form.Label>{label}{required && <span className="text-danger ms-1">*</span>}</Form.Label>}
      <InputGroup hasValidation>
        {Icon && <InputGroup.Text><Icon /></InputGroup.Text>}
        <Form.Control
          as={as}
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange} // The main handler works fine for these
          isInvalid={!!error}
          placeholder={placeholder}
          {...rest}
        />
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </InputGroup>
      {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
    </Form.Group>
  );
};

export default FormField;